import BaseFormController from "./BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "./FormField";
import {Option} from "ts-option";
import {isNullOrUndefined} from "util";
import * as moment from "moment";
import Email from "../model/email/Email";
import {isDefined} from "../utils/Utils";
import {isEmail} from "../utils/ValidationUtils";
import UkModulusChecking = require("uk-modulus-checking");
import * as validator from "validator";


export default class FormValidators {

	/**
	 * Currency format regex for xx.xx, (x), .xx formats.
	 * @type {RegExp}
	 */
	static CURRENCY_REGEX = /^((\d+(\.\d{0,2})?)|(\.\d{1,2})|(\d+(\.)?))$/;

	/**
	 * Phone number format regex.
	 * @type {RegExp}
	 */
	static PHONE_NUMBER_FORMAT_REGEX = /^\+?[\d ]+$/;

	/**
	 * Email validator.
	 * @param form form.
	 * @param value email.
	 * @return errors
	 */
	static email(form: BaseFormController<any>, value: string): string[] {
		if (isNullOrUndefined(value) || !isEmail(value))
			return ["Email is not valid"];
		else
			return [];
	}

	/**
	 * Emails validator.
	 * @param form form.
	 * @param value emails.
	 * @return errors
	 */
	static emails(form: BaseFormController<any>, value: Email[]): string[] {
		const errors: string[] = [];
		if (!isNullOrUndefined(value)) {
			value.forEach(item => {
				if (!isEmail(item.value)) {
					errors.push(`'${item.value}' Email is not valid`);
					return
				}
			})
		}

		return errors;
	}

	/**
	 * Email validator. It pass validation if email is not provided at all.
	 * @param {BaseFormController<any>} form
	 * @param {string} value email.
	 * @return {string[]} errors.
	 */
	static optionalEmail(form: BaseFormController<any>, value: string): string[] {
		if (!isNullOrUndefined(value) && !isEmail(value))
			return ["Email is not valid"];
		else
			return [];
	}

	/**
	 * Date in past validator. Validates moment if it is valid and not in future.
	 * @param {BaseFormController<any>} form
	 * @param {moment.Moment} value
	 * @return {string[]} List of errors.
	 */
	static dateInPast(form: BaseFormController<any>, value: moment.Moment): string[] {
		if (!isNullOrUndefined(value)) {
			if (value.isValid()) {
				if (value.isAfter(moment()))
					return ["Provided date is in future"];
				else
					return []
			}
			else
				return ["Provided date is invalid"];
		}
		else
			return [];
	}

	/**
	 * Create function validating if there is at least one element.
	 * @param errorMsg error message.
	 * @returns {errors} Errors to display.
	 */
	static atLeastOneItem(errorMsg: string) {
		return (form: BaseFormController<any>, items: any[]): string[] => {
			if (items.length > 0)
				return [];
			else return [errorMsg]
		}
	}

	/**
	 * Create function validating if string represent currency and is parsable to float.
	 * @param {string} errorMsg error message.
	 * @returns {errors} Errors to display.
	 */
	static isCurrencyFormat(errorMsg: string) {
		return (form: BaseFormController<any>, value: string) => {
			if (FormValidators.CURRENCY_REGEX.test(value)) {
				return [];
			}
			else {
				return [errorMsg];
			}
		}
	}

	/**
	 * Create function validating if value of the field isn't less than other one.
	 * @param errorMsg error message.
	 * @param otherFormFieldId id of other field to compare.
	 * @returns {errors} Errors to display.
	 */
	static notLessThan(errorMsg: string, otherFormFieldId: string) {
		return (form: BaseFormController<any>, thisValue: string): string[] => {
			const otherFormField: RequiredFormField<string> = form.findFieldById<RequiredFormField<string>>(otherFormFieldId).get;
			if (otherFormField.validate(form).errors.length == 0 && parseFloat(otherFormField.getValue()) > parseFloat(thisValue))
				return [errorMsg];
			else
				return [];
		}
	}

	/**
	 * Create function validating currency if value of the field isn't less than other one.
	 * @param errorMsg error message.
	 * @param otherFormFieldId id of other field to compare.
	 * @returns {errors} Errors to display.
	 */
	static notLessThanCurrency(errorMsg: string, otherFormFieldId: string) {
		return (form: BaseFormController<any>, thisValue: string): string[] => {
			const otherFormField: RequiredFormField<string> = form.findFieldById<RequiredFormField<string>>(otherFormFieldId).get;
			if (parseFloat(otherFormField.getOptionalValue().getOrElse("0")) === 0)
				return ["Price should be greater than 0"];
			else if (!FormValidators.CURRENCY_REGEX.test(thisValue))
				return ["Currency format: 10 / 10.00 / .00"];
			else if (FormValidators.CURRENCY_REGEX.test(thisValue) && otherFormField.validate(form).errors.length == 0 &&
				parseFloat(otherFormField.getValue()) > parseFloat(thisValue))
				return [errorMsg];
			else
				return [];
		}
	}

	/**
	 * Create function validating currency if value of the field isn't more than other one.
	 * @param errorMsg error message.
	 * @param otherFormFieldId id of other field to compare.
	 * @returns {errors} Errors to display.
	 */
	static notMoreThanCurrency(errorMsg: string, otherFormFieldId: string) {
		return (form: BaseFormController<any>, thisValue: string): string[] => {
			const otherFormField: RequiredFormField<string> = form.findFieldById<RequiredFormField<string>>(otherFormFieldId).get;
			if (!isNaN(parseFloat(thisValue)) && FormValidators.CURRENCY_REGEX.test(thisValue) &&
				otherFormField.validate(form).errors.length == 0 && parseFloat(otherFormField.getValue()) < parseFloat(thisValue))
				return [errorMsg];
			else
				return [];
		}
	}

	/**
	 * Create function validating if value of the boolean field is true.
	 * @param {string} errorMsg error message.
	 * @returns {errors} Errors to display.
	 */
	static isTrue(errorMsg: string) {
		return (form: BaseFormController<any>, value: boolean) => {
			if (value) {
				return [];
			}
			else {
				return [errorMsg];
			}
		}
	}

	/**
	 * Check if at least one of two form fields is not empty
	 * @param {string} errorsMsg error message to show.
	 * @param {string[]} otherFormFieldIds other form fields identifiers.
	 * @return {string[]} errors list.
	 */
	static atLeasOneRequired(errorsMsg: string, otherFormFieldIds: string[]) {
		return (form: BaseFormController<any>, thisValue: any): string[] => {
			let foundValue = false;
			otherFormFieldIds.forEach(otherFormFieldId => {
					const otherFormField: Option<OptionalFormField<any>> = form.findFieldById<OptionalFormField<any>>(
						otherFormFieldId);
					if (!isNullOrUndefined(otherFormField.get.value.orNull)) {
						foundValue = true;
						return;
					}
				}
			);
			return foundValue || !isNullOrUndefined(thisValue) ? [] : [errorsMsg];
		}
	}

	/**
	 * Check if given string is non empty.
	 * @param {string} errorMsg error message to show.
	 * @returns {string[]} errors list.
	 */
	static nonEmptyString(errorMsg: string) {
		return (form: BaseFormController<any>, thisValue: string): string[] => {
			if (thisValue && thisValue.trim().length > 0) {
				return [];
			}
			return [errorMsg]
		}
	}

	static isPhoneNumberFormat(errorMsg: string) {
		return (form: BaseFormController<any>, value: string) => {
			if (FormValidators.PHONE_NUMBER_FORMAT_REGEX.test(value)) {
				return [];
			}
			else {
				return [errorMsg];
			}
		}
	}

	static optionalWebsite(errorMsg: string) {
		return (form: BaseFormController<any>, value: string) => {
			if (isDefined(value) &&
				!validator.isURL(value, {protocols: ["http", "https"], require_host: true, require_protocol: true})) {
				return [errorMsg]
			}
			else {
				return [];
			}
		}
	}

	/**
	 * Validate if given number is between range given <min; max>
	 * @param errorMsg
	 * @param min
	 * @param max
	 */
	static numberRange(errorMsg: string, min: number, max: number) {
		return (form: BaseFormController<any>, value: number) => {
			if (value < min || value > max) {
				return [errorMsg];
			}
			return []
		}
	}

	static numberPositive(errorMsg: string) {
		return (form: BaseFormController<any>, value: number) => {
			if (value < 0) {
				return [errorMsg];
			}
			return []
		}
	}

	static maxLength(errorMsg: string, maxLength: number) {
		return (form: BaseFormController<any>, value: any) => {
			if (`${value}`.length > maxLength) {
				return [errorMsg];
			}
			return []
		}
	}

	static digitsOnly(errorMsg: string) {
		return (form: BaseFormController<any>, value: string) => {
			if (isNaN(Number(value))) {
				return [errorMsg];
			}
			return [];
		}
	}

	static isSortCodeBankDataValid(errorMsg: string, accountNoField: FormField<string>) {
		return (form: BaseFormController<any>, value: string) => {
			const accountNumber = accountNoField.getOptionalValue().getOrElse("");
			let isValid: boolean = false;
			if (value) {
				isValid = new UkModulusChecking({accountNumber, sortCode: value}).isValid();
			}
			if (!isValid) {
				return [errorMsg];
			}
			return [];
		}
	}

	static isAccountNoBankDataValid(errorMsg: string, sortCodeField: FormField<string>) {
		return (form: BaseFormController<any>, value: string) => {
			const sortCode = sortCodeField.getOptionalValue().getOrElse("");
			let isValid: boolean = false;
			if (value) {
				isValid = new UkModulusChecking({accountNumber: value, sortCode}).isValid();
			}
			if (!isValid) {
				return [errorMsg];
			}
			return [];
		}
	}

	static notLessThanValue(errorMsg: string, valueToCompare: number) {
		return (form: BaseFormController<any>, value: string) => {
			if (Number(value) < valueToCompare) {
				return [errorMsg];
			}
			return [];
		}
	}

	static notMoreThanValue(errorMsg: string, valueToCompare: number) {
		return (form: BaseFormController<any>, value: number) => {
			if (value > valueToCompare) {
				return [errorMsg];
			}
			return [];
		}
	}
}
